import React, { useState, useMemo, createContext, useContext } from 'react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { 
  Container, 
  Typography, 
  AppBar, 
  Toolbar, 
  Box, 
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  useMediaQuery,
  Button,
  Menu,
  MenuItem,
  Avatar,
  Divider,
  Collapse,
  ListItemButton,
  Chip,
  Switch,
  FormControlLabel,
  Tooltip
} from '@mui/material';
import {
  Menu as MenuIcon,
  AccountCircle as AccountIcon,
  Logout as LogoutIcon,
  Dashboard as DashboardIcon,
  PointOfSale as POSIcon,
  Hotel as ReservationsIcon,
  People as GuestsIcon,
  Analytics as ReportsIcon,
  Settings as SettingsIcon,
  Message as SMSIcon,
  Assessment as AnalyticsIcon,
  CleaningServices as HousekeepingIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon,
  CreditCard as BillingIcon,
  Map as MapIcon,
  TrendingUp as RevenueIcon,
  School as TrainingIcon,
  Speed as MeterIcon,
  Description as FormsIcon,
  AccountBalance as DepositsIcon,
  CalendarMonth as SeasonalIcon,
  Build as WorkOrderIcon,
  List as WaitingListIcon,
  Calculate as AccountingIcon,
  FolderShared as DocumentsIcon,
  Tune as CustomizationIcon,
  AccountBalance as FinancialIcon,
  Business as VendorIcon,
  CloudSync as OTAIcon,
  Security as SecurityIcon,
  CardGiftcard as GiftCardIcon,
  Navigation as DrivingDistanceIcon,
  Share as SocialMediaIcon,
  TouchApp as KioskIcon,
  CloudOff as OfflineIcon,
  Groups as GroupsIcon,
  Receipt as TaxIcon,
  TrendingUp as AdvancedPricingIcon,
  ExpandMore,
  ExpandLess,
  Business as TenantIcon,
  Business as BusinessIcon,
  DarkMode as DarkModeIcon,
  LightMode as LightModeIcon,
  Home as PropertyIcon,
  AttachMoney as FinanceIcon,
  Campaign as MarketingIcon,
  ManageAccounts as OperationsIcon,
  IntegrationInstructions as IntegrationIcon,
  AdminPanelSettings as AdminIcon,
  Brightness7 as SystemIcon,
} from '@mui/icons-material';
import DashboardPage from './pages/DashboardPage';
import POSPage from './pages/POSPage';
import SMSPage from './pages/SMSPage';
import AnalyticsPage from './pages/AnalyticsPage';
import HousekeepingPage from './pages/HousekeepingPage';
import EmailPage from './pages/EmailPage';
import LocationsPage from './pages/LocationsPage';
import BillingPage from './pages/BillingPage';
import SiteMapPage from './pages/SiteMapPage';
import RevenueOptimizationPage from './pages/RevenueOptimizationPage';
import TrainingPage from './pages/TrainingPage';
import MeterReadingPage from './pages/MeterReadingPage';
import FormsPage from './pages/FormsPage';
import DepositsPage from './pages/DepositsPage';
import SeasonalPage from './pages/SeasonalPage';
import WorkOrdersPage from './pages/WorkOrdersPage';
import WaitingListPage from './pages/WaitingListPage';
import CustomizationPage from './pages/CustomizationPage';
import FinancialPage from './pages/FinancialPage';
import VendorPage from './pages/VendorPage';
import ReportsPage from './pages/ReportsPage';
import LoginPage from './pages/LoginPage';
import ReservationsPage from './pages/ReservationsPage';
import GuestsPage from './pages/GuestsPage';
import SettingsPage from './pages/SettingsPage';
import GateAccessPage from './pages/GateAccessPage';
import OTAManagement from './components/ota/OTAManagement';
import IntegrationsPage from './pages/IntegrationsPage';
import GiftCardsPage from './pages/GiftCardsPage';
import DrivingDistancePage from './pages/DrivingDistancePage';
import SocialMediaPage from './pages/SocialMediaPage';
import KioskManagementPage from './pages/KioskManagementPage';
import OfflineModePage from './pages/OfflineModePage';
import GroupReservationsPage from './pages/GroupReservationsPage';
import TaxManagementPage from './pages/TaxManagementPage';
import AccountingPage from './pages/AccountingPage';
import GuestDocumentsPage from './pages/GuestDocumentsPage';
import EmailProvidersPage from './pages/EmailProvidersPage';
import AdvancedPricingPage from './pages/AdvancedPricingPage';
import TenantsPage from './pages/platform/TenantsPage';
import PlatformBillingDashboard from './pages/platform/PlatformBillingDashboard';
import api from './services/api';

// Theme Context
const ThemeContext = createContext<{
  darkMode: boolean;
  toggleDarkMode: () => void;
}>({
  darkMode: false,
  toggleDarkMode: () => {},
});

export const useTheme = () => useContext(ThemeContext);

// Organized menu structure with categories
const menuCategories = [
  {
    id: 'overview',
    text: 'Overview',
    icon: <DashboardIcon />,
    items: [
      { text: 'Dashboard', icon: <DashboardIcon />, component: 'dashboard' },
      { text: 'Analytics', icon: <AnalyticsIcon />, component: 'analytics' },
      { text: 'Reports', icon: <ReportsIcon />, component: 'reports' },
    ]
  },
  {
    id: 'properties',
    text: 'Properties & Units',
    icon: <PropertyIcon />,
    items: [
      { text: 'Locations', icon: <LocationIcon />, component: 'locations' },
      { text: 'Site Map', icon: <MapIcon />, component: 'sitemap' },
      { text: 'Gate Access', icon: <SecurityIcon />, component: 'gateaccess' },
      { text: 'Meter Reading', icon: <MeterIcon />, component: 'meters' },
    ]
  },
  {
    id: 'reservations',
    text: 'Reservations & Guests',
    icon: <ReservationsIcon />,
    items: [
      { text: 'Reservations', icon: <ReservationsIcon />, component: 'reservations' },
      { text: 'Group Reservations', icon: <GroupsIcon />, component: 'group-reservations' },
      { text: 'Guests', icon: <GuestsIcon />, component: 'guests' },
      { text: 'Guest Documents', icon: <DocumentsIcon />, component: 'guest-documents' },
      { text: 'Waiting List', icon: <WaitingListIcon />, component: 'waitinglist' },
    ]
  },
  {
    id: 'finance',
    text: 'Finance & Billing',
    icon: <FinanceIcon />,
    items: [
      { text: 'Billing', icon: <BillingIcon />, component: 'billing' },
      { text: 'Point of Sale', icon: <POSIcon />, component: 'pos' },
      { text: 'Gift Cards', icon: <GiftCardIcon />, component: 'giftcards' },
      { text: 'Deposits & Accounts', icon: <DepositsIcon />, component: 'deposits' },
      { text: 'Financial', icon: <FinancialIcon />, component: 'financial' },
      { text: 'Accounting', icon: <AccountingIcon />, component: 'accounting' },
      { text: 'Tax Management', icon: <TaxIcon />, component: 'tax' },
    ]
  },
  {
    id: 'revenue',
    text: 'Revenue Management',
    icon: <RevenueIcon />,
    items: [
      { text: 'Revenue Optimization', icon: <RevenueIcon />, component: 'revenue' },
      { text: 'Advanced Pricing', icon: <AdvancedPricingIcon />, component: 'advanced-pricing' },
      { text: 'Seasonal Management', icon: <SeasonalIcon />, component: 'seasonal' },
    ]
  },
  {
    id: 'marketing',
    text: 'Marketing & Communications',
    icon: <MarketingIcon />,
    items: [
      { text: 'Email Marketing', icon: <EmailIcon />, component: 'email' },
      { text: 'SMS Messaging', icon: <SMSIcon />, component: 'sms' },
      { text: 'Social Media', icon: <SocialMediaIcon />, component: 'social-media' },
      { text: 'Email Providers', icon: <EmailIcon />, component: 'email-providers' },
    ]
  },
  {
    id: 'operations',
    text: 'Operations',
    icon: <OperationsIcon />,
    items: [
      { text: 'Housekeeping', icon: <HousekeepingIcon />, component: 'housekeeping' },
      { text: 'Work Orders', icon: <WorkOrderIcon />, component: 'workorders' },
      { text: 'Training', icon: <TrainingIcon />, component: 'training' },
      { text: 'Forms & Documents', icon: <FormsIcon />, component: 'forms' },
      { text: 'Vendors', icon: <VendorIcon />, component: 'vendors' },
    ]
  },
  {
    id: 'integrations',
    text: 'Integrations',
    icon: <IntegrationIcon />,
    items: [
      { text: 'All Integrations', icon: <OTAIcon />, component: 'integrations' },
      { text: 'Kiosk Mode', icon: <KioskIcon />, component: 'kiosk' },
      { text: 'Offline Mode', icon: <OfflineIcon />, component: 'offline' },
      { text: 'Driving Distance', icon: <DrivingDistanceIcon />, component: 'driving-distance' },
    ]
  },
  {
    id: 'system',
    text: 'System',
    icon: <SystemIcon />,
    items: [
      { text: 'Settings', icon: <SettingsIcon />, component: 'settings' },
      { text: 'Customization', icon: <CustomizationIcon />, component: 'customization' },
    ]
  },
];

// Platform admin specific menu category
const platformMenuCategory = {
  id: 'platform',
  text: 'Platform Admin',
  icon: <AdminIcon />,
  items: [
    { text: 'Tenants', icon: <TenantIcon />, component: 'tenants' },
    { text: 'Platform Billing', icon: <BillingIcon />, component: 'platform-billing' },
    { text: 'Platform Analytics', icon: <AnalyticsIcon />, component: 'platform-analytics' },
    { text: 'Platform Settings', icon: <SettingsIcon />, component: 'platform-settings' },
  ]
};

function AppContent() {
  const { user, logout, tenantId, switchTenant } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeComponent, setActiveComponent] = useState('dashboard');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [tenantMenuAnchor, setTenantMenuAnchor] = useState<null | HTMLElement>(null);
  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem('darkMode');
    return saved === 'true';
  });
  const [expandedCategories, setExpandedCategories] = useState<string[]>(['overview']);
  const [tenants, setTenants] = useState<any[]>([]);

  const theme = useMemo(() => createTheme({
    palette: {
      mode: darkMode ? 'dark' : 'light',
      primary: {
        main: darkMode ? '#90caf9' : '#1976d2',
      },
      secondary: {
        main: darkMode ? '#f48fb1' : '#dc004e',
      },
      background: {
        default: darkMode ? '#121212' : '#f5f5f5',
        paper: darkMode ? '#1e1e1e' : '#ffffff',
      },
    },
    components: {
      MuiDrawer: {
        styleOverrides: {
          paper: {
            backgroundColor: darkMode ? '#1e1e1e' : '#ffffff',
            backgroundImage: 'none',
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
          },
        },
      },
    },
  }), [darkMode]);

  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const toggleDarkMode = () => {
    const newMode = !darkMode;
    setDarkMode(newMode);
    localStorage.setItem('darkMode', String(newMode));
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    handleClose();
  };

  const toggleCategory = (categoryId: string) => {
    setExpandedCategories(prev => 
      prev.includes(categoryId) 
        ? prev.filter(id => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  // Fetch available tenants for platform admin
  React.useEffect(() => {
    if (user?.isPlatformAdmin) {
      api.get('/platform/tenants')
        .then(response => setTenants(response.data.tenants || []))
        .catch(console.error);
    }
  }, [user]);

  if (!user) {
    return <LoginPage />;
  }

  const renderActiveComponent = () => {
    switch (activeComponent) {
      case 'dashboard':
        return <DashboardPage />;
      case 'locations':
        return <LocationsPage />;
      case 'gateaccess':
        return <GateAccessPage />;
      case 'sitemap':
        return <SiteMapPage />;
      case 'revenue':
        return <RevenueOptimizationPage />;
      case 'advanced-pricing':
        return <AdvancedPricingPage />;
      case 'billing':
        return <BillingPage />;
      case 'pos':
        return <POSPage />;
      case 'giftcards':
        return <GiftCardsPage />;
      case 'sms':
        return <SMSPage />;
      case 'email':
        return <EmailPage />;
      case 'email-providers':
        return <EmailProvidersPage />;
      case 'social-media':
        return <SocialMediaPage />;
      case 'analytics':
        return <AnalyticsPage />;
      case 'driving-distance':
        return <DrivingDistancePage />;
      case 'housekeeping':
        return <HousekeepingPage />;
      case 'training':
        return <TrainingPage />;
      case 'meters':
        return <MeterReadingPage />;
      case 'forms':
        return <FormsPage />;
      case 'deposits':
        return <DepositsPage />;
      case 'seasonal':
        return <SeasonalPage />;
      case 'workorders':
        return <WorkOrdersPage />;
      case 'waitinglist':
        return <WaitingListPage />;
      case 'customization':
        return <CustomizationPage />;
      case 'financial':
        return <FinancialPage />;
      case 'vendors':
        return <VendorPage />;
      case 'integrations':
        return <IntegrationsPage />;
      case 'reservations':
        return <ReservationsPage />;
      case 'guests':
        return <GuestsPage />;
      case 'guest-documents':
        return <GuestDocumentsPage />;
      case 'reports':
        return <ReportsPage />;
      case 'kiosk':
        return <KioskManagementPage />;
      case 'offline':
        return <OfflineModePage />;
      case 'group-reservations':
        return <GroupReservationsPage />;
      case 'tax':
        return <TaxManagementPage />;
      case 'accounting':
        return <AccountingPage />;
      case 'settings':
        return <SettingsPage />;
      case 'tenants':
        return <TenantsPage />;
      case 'platform-billing':
        return <PlatformBillingDashboard />;
      case 'platform-analytics':
        return <Typography variant="h4" sx={{ p: 3 }}>Platform Analytics (Coming Soon)</Typography>;
      case 'platform-settings':
        return <Typography variant="h4" sx={{ p: 3 }}>Platform Settings (Coming Soon)</Typography>;
      default:
        return <DashboardPage />;
    }
  };

  const drawer = (
    <Box sx={{ width: 280, height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Toolbar sx={{ px: 2 }}>
        <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 600 }}>
          Equity Engine
        </Typography>
      </Toolbar>
      <Divider />
      
      <Box sx={{ flexGrow: 1, overflow: 'auto', py: 1 }}>
        {/* Platform Admin Section */}
        {user?.isPlatformAdmin && (
          <>
            <List dense>
              <ListItemButton
                onClick={() => toggleCategory('platform')}
                sx={{ py: 1.5, px: 2 }}
              >
                <ListItemIcon>{platformMenuCategory.icon}</ListItemIcon>
                <ListItemText 
                  primary={platformMenuCategory.text}
                  primaryTypographyProps={{ fontWeight: 600 }}
                />
                <Chip 
                  label="Admin" 
                  size="small" 
                  color="error" 
                  sx={{ mr: 1 }}
                />
                {expandedCategories.includes('platform') ? <ExpandLess /> : <ExpandMore />}
              </ListItemButton>
              <Collapse in={expandedCategories.includes('platform')} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  {platformMenuCategory.items.map((item) => (
                    <ListItemButton
                      key={item.text}
                      selected={activeComponent === item.component}
                      onClick={() => {
                        setActiveComponent(item.component);
                        if (isMobile) setMobileOpen(false);
                      }}
                      sx={{ pl: 4, py: 0.75 }}
                    >
                      <ListItemIcon sx={{ minWidth: 40 }}>
                        {item.icon}
                      </ListItemIcon>
                      <ListItemText primary={item.text} />
                    </ListItemButton>
                  ))}
                </List>
              </Collapse>
            </List>
            <Divider sx={{ my: 1 }} />
          </>
        )}

        {/* Regular Menu Categories */}
        <List dense>
          {menuCategories.map((category) => {
            // Filter categories based on user role
            const visibleItems = category.items.filter(item => {
              // Role-based filtering logic here
              if (user.role === 'staff') {
                // Staff can only see certain pages
                const staffAllowed = ['dashboard', 'reservations', 'guests', 'pos'];
                return staffAllowed.includes(item.component);
              }
              if (user.role === 'manager') {
                // Managers can't see system settings
                const managerRestricted = ['settings', 'customization'];
                return !managerRestricted.includes(item.component);
              }
              return true; // Admin and platform admin see everything
            });

            if (visibleItems.length === 0) return null;

            return (
              <React.Fragment key={category.id}>
                <ListItemButton
                  onClick={() => toggleCategory(category.id)}
                  sx={{ py: 1.5, px: 2 }}
                >
                  <ListItemIcon>{category.icon}</ListItemIcon>
                  <ListItemText 
                    primary={category.text}
                    primaryTypographyProps={{ fontWeight: 600 }}
                  />
                  {expandedCategories.includes(category.id) ? <ExpandLess /> : <ExpandMore />}
                </ListItemButton>
                <Collapse in={expandedCategories.includes(category.id)} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {visibleItems.map((item) => (
                      <ListItemButton
                        key={item.text}
                        selected={activeComponent === item.component}
                        onClick={() => {
                          setActiveComponent(item.component);
                          if (isMobile) setMobileOpen(false);
                        }}
                        sx={{ pl: 4, py: 0.75 }}
                      >
                        <ListItemIcon sx={{ minWidth: 40 }}>
                          {item.icon}
                        </ListItemIcon>
                        <ListItemText 
                          primary={item.text}
                          primaryTypographyProps={{ fontSize: '0.875rem' }}
                        />
                      </ListItemButton>
                    ))}
                  </List>
                </Collapse>
              </React.Fragment>
            );
          })}
        </List>
      </Box>

      {/* Theme Toggle at Bottom */}
      <Divider />
      <Box sx={{ p: 2 }}>
        <FormControlLabel
          control={
            <Switch
              checked={darkMode}
              onChange={toggleDarkMode}
              icon={<LightModeIcon />}
              checkedIcon={<DarkModeIcon />}
            />
          }
          label={darkMode ? 'Dark Mode' : 'Light Mode'}
          sx={{ m: 0 }}
        />
      </Box>
    </Box>
  );

  return (
    <ThemeContext.Provider value={{ darkMode, toggleDarkMode }}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box sx={{ display: 'flex' }}>
          <AppBar position="fixed" sx={{ zIndex: theme.zIndex.drawer + 1 }}>
            <Toolbar>
              {isMobile && (
                <IconButton
                  color="inherit"
                  edge="start"
                  onClick={handleDrawerToggle}
                  sx={{ mr: 2 }}
                >
                  <MenuIcon />
                </IconButton>
              )}
              
              {/* Breadcrumb / Page Title */}
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="h6" component="div">
                  {user?.isPlatformAdmin && !tenantId ? (
                    'Platform Administration'
                  ) : (
                    <>
                      {tenants.find(t => t._id === tenantId)?.name || 'Admin Portal'}
                      {activeComponent !== 'dashboard' && (
                        <Typography component="span" variant="body2" sx={{ ml: 1, opacity: 0.8 }}>
                          / {menuCategories.flatMap(c => c.items).find(i => i.component === activeComponent)?.text || 
                              platformMenuCategory.items.find(i => i.component === activeComponent)?.text}
                        </Typography>
                      )}
                    </>
                  )}
                </Typography>
              </Box>

              {/* Tenant Switcher for Platform Admin */}
              {user?.isPlatformAdmin && (
                <Box sx={{ mr: 2 }}>
                  <Button
                    color="inherit"
                    variant="outlined"
                    size="small"
                    onClick={(e) => setTenantMenuAnchor(e.currentTarget)}
                    startIcon={<BusinessIcon />}
                    sx={{ 
                      borderColor: 'rgba(255,255,255,0.3)',
                      '&:hover': { borderColor: 'rgba(255,255,255,0.5)' }
                    }}
                  >
                    {tenantId ? tenants.find(t => t._id === tenantId)?.subdomain || 'Select Tenant' : 'Platform View'}
                  </Button>
                  <Menu
                    anchorEl={tenantMenuAnchor}
                    open={Boolean(tenantMenuAnchor)}
                    onClose={() => setTenantMenuAnchor(null)}
                  >
                    <MenuItem
                      onClick={() => {
                        if (switchTenant) switchTenant('');
                        setTenantMenuAnchor(null);
                      }}
                    >
                      <ListItemIcon>
                        <AdminIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText>Platform View</ListItemText>
                    </MenuItem>
                    <Divider />
                    {tenants.map((tenant) => (
                      <MenuItem
                        key={tenant._id}
                        onClick={() => {
                          if (switchTenant) switchTenant(tenant._id);
                          setTenantMenuAnchor(null);
                        }}
                        selected={tenantId === tenant._id}
                      >
                        <ListItemIcon>
                          <TenantIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>
                          {tenant.name}
                          <Typography variant="caption" display="block" color="text.secondary">
                            {tenant.subdomain}.equityengine.com
                          </Typography>
                        </ListItemText>
                      </MenuItem>
                    ))}
                  </Menu>
                </Box>
              )}

              {/* User Menu */}
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Tooltip title="Toggle theme">
                  <IconButton color="inherit" onClick={toggleDarkMode} sx={{ mr: 1 }}>
                    {darkMode ? <LightModeIcon /> : <DarkModeIcon />}
                  </IconButton>
                </Tooltip>
                
                <Button
                  color="inherit"
                  onClick={handleMenu}
                  startIcon={
                    <Avatar sx={{ width: 32, height: 32, bgcolor: 'secondary.main' }}>
                      {user.firstName?.[0]}{user.lastName?.[0]}
                    </Avatar>
                  }
                >
                  {user.firstName} {user.lastName}
                </Button>
                <Menu
                  anchorEl={anchorEl}
                  open={Boolean(anchorEl)}
                  onClose={handleClose}
                >
                  <Box sx={{ px: 2, py: 1 }}>
                    <Typography variant="subtitle2">
                      {user.firstName} {user.lastName}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {user.email}
                    </Typography>
                    <Chip 
                      label={user.isPlatformAdmin ? 'Platform Admin' : user.role} 
                      size="small" 
                      color={user.isPlatformAdmin ? 'error' : 'primary'}
                      sx={{ mt: 1 }}
                    />
                  </Box>
                  <Divider />
                  <MenuItem onClick={handleLogout}>
                    <ListItemIcon>
                      <LogoutIcon fontSize="small" />
                    </ListItemIcon>
                    Logout
                  </MenuItem>
                </Menu>
              </Box>
            </Toolbar>
          </AppBar>

          <Drawer
            variant={isMobile ? 'temporary' : 'permanent'}
            open={isMobile ? mobileOpen : true}
            onClose={handleDrawerToggle}
            ModalProps={{
              keepMounted: true,
            }}
            sx={{
              width: 280,
              flexShrink: 0,
              '& .MuiDrawer-paper': {
                width: 280,
                boxSizing: 'border-box',
              },
            }}
          >
            {drawer}
          </Drawer>

          <Box
            component="main"
            sx={{
              flexGrow: 1,
              width: { md: `calc(100% - 280px)` },
              minHeight: '100vh',
              backgroundColor: 'background.default',
            }}
          >
            <Toolbar />
            <Box sx={{ p: { xs: 2, sm: 3 } }}>
              {renderActiveComponent()}
            </Box>
          </Box>
        </Box>
      </ThemeProvider>
    </ThemeContext.Provider>
  );
}

function App() {
  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </LocalizationProvider>
  );
}

export default App;